import { CloseIcon, SearchIcon } from '@panwds/icons';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import { useOnOutsideClick } from '../../utils/useOnOutsideClick/useOnOutsideClick.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { useDimensions } from '../../utils/useDimensions/useDimensions.esm.js';
import { Input } from '../Input/Input.esm.js';
import { List } from '../List/List.esm.js';
import { ListItem } from '../List/ListItem.esm.js';
import { Portal } from '../Portal/Portal.esm.js';
import { Body } from '../Typography/Body.esm.js';
import '@panwds/tailwindcss';
import '../../utils/constants.esm.js';
import '@popperjs/core';
import { useTypeAhead } from './useTypeAhead.esm.js';

function Typeahead({ ariadescribedby, arialabel, arialabelledby, autoFocus = false, boldTyped = false, showClearIcon = false, appearance = 'default', defaultValue = '', disabled = false, error = false, startDecorator, endDecorator, filterOptions = (value, options) => {
    return options.filter(({ label }) => label === null || label === void 0 ? void 0 : label.toLowerCase().includes(value === null || value === void 0 ? void 0 : value.toLowerCase()));
}, addInputClassName, addDropdownClassName, loading = false, noOptionsText, loadingIndicator, onClose, onHighlightChange, onInputChange, onChange, onKeyDown, onKeyUp, open = false, onOpen, options = [], placeholder = 'Search', size = 'md', errorMessage = 'Error', showCategories, onClear, dataMetrics, dataTestId = '' }) {
    const intl = useIntl();
    (noOptionsText =
        noOptionsText !== null && noOptionsText !== void 0 ? noOptionsText : intl.formatMessage({
            defaultMessage: 'No results found',
            id: 'tsIjZx',
            description: 'No search results were found'
        })),
        (loadingIndicator =
            loadingIndicator !== null && loadingIndicator !== void 0 ? loadingIndicator : intl.formatMessage({
                defaultMessage: 'Loading',
                id: 'UzCLgr',
                description: 'Loading search results'
            }));
    const [activeIndex, setActiveIndex] = useState(-1);
    const [inputValue, setInputValue] = useState(defaultValue);
    const dropdownRef = useRef(null);
    const internalRef = useRef(null);
    const dimensions = useDimensions({ ref: internalRef });
    const inputRef = useRef(null);
    const [popupOpen, setPopupOpen] = useState(open);
    const allOptions = useMemo(() => options.flatMap((item) => item.options), [options]);
    // TODO: potential bug, need to narrow allOptions type
    const [filteredOptions, setFilteredOptions] = useState(allOptions);
    /**
     * allow changes to defaultValue into componentDidUpdate react lifecycle
     */
    useEffect(() => {
        setInputValue(defaultValue);
    }, [defaultValue]);
    const { handleFocus, handleItemClick, handleInputChange, handleHighlightChange, handleKeyDown, handleKeyUp } = useTypeAhead({
        activeIndex,
        allOptions,
        filteredOptions,
        inputRef,
        popupOpen,
        options,
        onClose,
        onOpen,
        onHighlightChange,
        onInputChange,
        onChange,
        onKeyDown,
        onKeyUp,
        filterOptions,
        setActiveIndex,
        setFilteredOptions,
        setInputValue,
        setPopupOpen
    });
    const InputClassName = classNames(addInputClassName, 'tw-w-full', size === 'lg' ? 'tw-text-sm' : 'tw-text-xs');
    const loadingClassName = classNames('tw-py-1 tw-pl-4 tw-border-b tw-border-r-0 tw-border-t-0 tw-border-l-0 tw-border-gray-200 tw-border-solid hover:tw-bg-gray-200 focus:tw-bg-gray-200 hover:tw-cursor-pointer', size === 'lg' ? 'tw-text-sm' : 'tw-text-xs');
    const noOptionsClassName = classNames('tw-py-1 tw-pl-4 tw-border-b tw-border-r-0 tw-border-t-0 tw-border-l-0 tw-border-gray-200 tw-border-solid hover:tw-bg-gray-200 tw-pointer-events-none', size === 'lg' ? 'tw-text-sm' : 'tw-text-xs');
    const { triggerRef, containerRef } = usePopper({
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 2]
                }
            }
        ]
    });
    const renderGroup = (obj) => {
        return (React.createElement("div", { key: obj.key, className: 'tw-flex tw-flex-col tw-max-h-44' },
            React.createElement("div", { className: classNames(' tw-overflow-scroll tw-scrollbar-small dark:tw-scrollbar-track-dark dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark', 'tw-pl-3 tw-pt-2 tw-pb-0.5 tw-text-gray-400 dark:tw-bg-gray-1000 tw-bg-white', 'tw-border-b tw-border-r-0 tw-border-t-0 tw-border-l-0 tw-border-gray-200 dark:tw-border-gray-700 tw-border-solid', size === 'lg' ? 'tw-text-sm' : 'tw-text-xs') },
                obj.group,
                obj.children)));
    };
    const renderListOption = (options, icon) => {
        const filtered = options.filter(({ label }) => filteredOptions.map(({ label }) => label).indexOf(label) > -1);
        return filtered.map((option) => {
            var _a;
            const { label, id } = option;
            const isDisabled = option['isDisabled'];
            const optionArray = label.split(RegExp(inputValue, 'ig'));
            const match = label.match(RegExp(inputValue, 'ig'));
            return (React.createElement(ListItem, { key: id || label, "data-testid": id || label, active: ((_a = filteredOptions[activeIndex]) === null || _a === void 0 ? void 0 : _a.label) === label, addClassName: classNames('tw-pl-3', isDisabled && 'tw-text-disabled tw-cursor-not-allowed'), listItemStyle: size === 'lg'
                    ? {
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        paddingLeft: '1rem'
                    }
                    : {}, iconLeft: icon, onClick: () => handleItemClick(option) },
                React.createElement("span", null, inputValue !== ''
                    ? optionArray.map((item, index) => !boldTyped ? (React.createElement("span", { key: index },
                        React.createElement("strong", null, item),
                        index !== optionArray.length - 1 && match && match[index])) : (React.createElement("span", { key: index },
                        item,
                        index !== optionArray.length - 1 && match && React.createElement("strong", null, match[index]))))
                    : option['label'])));
        });
    };
    // On clicking outside close the popup and call an onClose callback if passed via props when popUp is open
    useOnOutsideClick([dropdownRef, internalRef], () => {
        onClose && popupOpen && onClose();
        setPopupOpen(false);
    });
    const noOptionBlock = (React.createElement(ListItem, { key: "no-options", addClassName: classNames('tw-pl-3', noOptionsClassName), listItemStyle: size === 'lg' ? { fontSize: '0.875rem', lineHeight: '1.25rem' } : {} }, noOptionsText));
    const loadingBlock = (React.createElement(ListItem, { key: "loading", addClassName: classNames('tw-pl-3', loadingClassName), listItemStyle: size === 'lg' ? { fontSize: '0.875rem', lineHeight: '1.25rem' } : {} }, loadingIndicator));
    const uniqueTestId = dataTestId && `-${dataTestId}`;
    const clearButton = (React.createElement("span", { role: "button", "aria-label": "Clear", onClick: () => {
            setInputValue('');
            setFilteredOptions(allOptions);
            onClear === null || onClear === void 0 ? void 0 : onClear();
        } },
        React.createElement(CloseIcon, { size: size === 'sm' ? 'xs' : 'sm', className: "tw-text-gray-600 dark:tw-text-dark-bg tw-cursor-pointer tw-flex", "data-testid": `close-icon${uniqueTestId}` })));
    const searchIcon = (React.createElement(SearchIcon, { size: size === 'sm' ? 'xs' : 'sm', className: "tw-text-gray-600 dark:tw-text-dark-bg tw-cursor-pointer tw-flex", "data-testid": `search-icon${uniqueTestId}}` }));
    return (React.createElement("div", { className: "tw-flex tw-flex-col" },
        React.createElement("div", { ref: mergeRefs([internalRef, triggerRef]), "aria-describedby": ariadescribedby, "aria-labelledby": arialabelledby, "aria-label": arialabel || 'Input' },
            React.createElement(Input, { disabled: disabled, addClassName: InputClassName, appearance: error ? 'error' : appearance, placeholder: placeholder, prefix: startDecorator ? startDecorator : searchIcon, suffix: React.createElement("div", { className: "tw-flex tw-gap-2 tw-items-center" },
                    endDecorator,
                    showClearIcon && inputValue !== '' && clearButton), onChange: handleInputChange, onClick: () => handleHighlightChange(inputRef), onKeyUp: handleKeyUp, onKeyDown: handleKeyDown, onFocus: handleFocus, autoFocus: autoFocus, value: inputValue, ref: inputRef, role: 'textbox', autoComplete: "off", dataMetrics: dataMetrics, dataTestId: dataTestId }),
            error && React.createElement(Body, { addClassName: "tw-text-red-500 tw-p-0" }, errorMessage)),
        popupOpen && !error && (React.createElement(Portal, null,
            React.createElement("div", { key: 'presentation', role: "presentation", ref: containerRef, className: addDropdownClassName, style: { width: `${dimensions.width}px` } }, options.length > 0 && (React.createElement(List, { isOpen: popupOpen, ref: dropdownRef }, !loading && filteredOptions.length === 0
                ? noOptionBlock
                : loading
                    ? loadingBlock
                    : options.map((item) => {
                        if (showCategories) {
                            return renderGroup({
                                key: item.key,
                                group: item.group,
                                children: renderListOption(item.options, item.icon)
                            });
                        }
                        return renderListOption(item.options, item.icon);
                    }))))))));
}

export { Typeahead };
//# sourceMappingURL=TypeAhead.esm.js.map
